/**
 * Loonstroken-module (spec B) — types, DTO's en Zod-schemas.
 *
 * Persoonsgebonden financiele documenten (loonstrook / jaaropgave). De PDF
 * leeft als `Document` (scope EMPLOYEE, categorie LOONSTROOK/JAAROPGAVE); de
 * `Loonstrook`-rij draagt de periode-, match- en workflow-semantiek.
 *
 * BELANGRIJK (AVG, batch-9 #28): BSN wordt NOOIT in een DTO of log opgenomen.
 * Loonbedragen worden sinds #28 WEL getoond - bruto/netto op de lijst en de
 * volledige bedragen + uren op het detail - maar uitsluitend achter de
 * afscherming (backoffice: PAYROLL + magLoonstrokenZien; portal: alleen de
 * eigen, klaargezette strook). Lijst-/detail-DTO's bevatten daarom bedragen,
 * maar NOOIT in logs en NOOIT richting `apps/klant`. `matchSleutel` bewaart enkel
 * de sleutel-NAAM ("handmatig" | "employeeNummer" | "bsn" | "naam-fuzzy").
 */

import { z } from 'zod'
import { idParam } from './id-validators'
import { NMBRS_KOLOM_CODES } from './nmbrs-export'
// batch-59 loonstrook-controles (FO-433) - de afwijkingen die de controle op
// deze strook vond, meegeleverd op het strook-detail.
import type { LoonstrookControleAfwijkingDTO } from './loonstrook-controles'

// ─── Enums (matching Prisma enums) ────────────────────────────────────────────

export const LOONSTROOK_SOORT = ['LOONSTROOK', 'JAAROPGAVE'] as const
export type LoonstrookSoort = (typeof LOONSTROOK_SOORT)[number]

export const LOONSTROOK_STATUS = [
  'CONCEPT',
  'GEPLAND',
  'KLAARGEZET',
  'INGETROKKEN',
  // batch-56 (FO-417) — afgekeurd bij de controle; wacht op de gecorrigeerde
  // uitgave van de salarisverwerker. Niet klaarzetbaar zolang dit geldt.
  'AFGEKEURD',
] as const
export type LoonstrookStatus = (typeof LOONSTROOK_STATUS)[number]

// ─── batch-56 loonstroken (FO-422) — run-begrip ───────────────────────────────

/** Run-soort binnen een periode: de reguliere run (1) of een extra run (2..n). */
export const LOONSTROOK_RUN_TYPE = ['HOOFDRUN', 'EXTRA_RUN'] as const
export type LoonstrookRunType = (typeof LOONSTROOK_RUN_TYPE)[number]

/**
 * Keuze bij een tweede strook voor dezelfde medewerker + periode zonder leesbare
 * run-aanduiding (spec §3.2). `TOEVOEGEN` = extra run naast de bestaande strook
 * (DEFAULT - er raakt nooit iets kwijt); `CORRECTIE` = gecorrigeerde uitgave van
 * de bestaande run, die als nieuwe VERSIE over die run heen gaat.
 */
export const LOONSTROOK_RUN_KEUZE = ['TOEVOEGEN', 'CORRECTIE'] as const
export type LoonstrookRunKeuze = (typeof LOONSTROOK_RUN_KEUZE)[number]

// ─── batch-56 loonstroken (FO-417) — afkeuren + verzoek-beoordeling ──────────

/** Categorie van één afkeur-bevinding (rapportage per categorie later mogelijk). */
export const LOONSTROOK_BEVINDING_CATEGORIE = [
  'UREN',
  'TOESLAG',
  'VERGOEDING',
  'INHOUDING',
  'PERSOONSGEGEVENS',
  'OVERIG',
] as const
export type LoonstrookBevindingCategorie = (typeof LOONSTROOK_BEVINDING_CATEGORIE)[number]

/** Uitkomst van de beoordeling van een medewerker-verzoek. */
export const LOONSTROOK_VERZOEK_BESLUIT = ['GOEDGEKEURD', 'AFGEWEZEN'] as const
export type LoonstrookVerzoekBesluit = (typeof LOONSTROOK_VERZOEK_BESLUIT)[number]

export const LOONSTROOK_MATCH_STATUS = [
  'ONGEMATCHT',
  'AI_VOORGESTELD',
  'BEVESTIGD',
  'HANDMATIG',
] as const
export type LoonstrookMatchStatus = (typeof LOONSTROOK_MATCH_STATUS)[number]

export const LOONSTROOK_IMPORT_STATUS = [
  'GEUPLOAD',
  'GEANALYSEERD',
  'VERWERKT',
  'MISLUKT',
] as const
export type LoonstrookImportStatus = (typeof LOONSTROOK_IMPORT_STATUS)[number]

export const LOONSTROOK_PERIODE_TYPE = ['MONTH', 'FOUR_WEEK'] as const
export type LoonstrookPeriodeType = (typeof LOONSTROOK_PERIODE_TYPE)[number]

// ─── Component-regels van een loonstrook (WGV-inkomen-bron) ───────────────────
//
// Naast de scalaire totalen (LoonstrookBedragenDTO) leggen we sinds de
// WGV-inkomen-feature de losse regels van de strook vast (per categorie, met het
// periode- en cumulatief-bedrag). Hieruit aggregeert de werkgeversverklaring het
// structurele variabele inkomen (overwerk / onregelmatigheidstoeslag / reisuren).
// Opslag als JSON op `Loonstrook`, achter dezelfde 2-persoons-afscherming als de
// bedragen (gevoelig salarisdetail; NOOIT richting apps/klant, NOOIT in logs).

/** Categorie van een loonstrook-regel, genormaliseerd over CAO-/provider-varianten. */
export const LOONSTROOK_COMPONENT_CATEGORIE = [
  'BASIS', // stam-/maandsalaris
  'OVERWERK', // Overwerk 150%, Overuren 100%, ...
  'ONREGELMATIGHEID', // ploeg-/zaterdag-/zondag-/feestdag-/toeslaguren
  'REISUREN', // betaalde reistijd (belast) -> telt mee
  'REISVERGOEDING', // km-/reiskostenvergoeding (onbelast) -> telt NIET mee
  'PROVISIE',
  'EINDEJAAR', // 13e maand / eindejaarsuitkering (informatief)
  'VAKANTIEGELD', // informatief; komt uit %-cascade
  'BIJTELLING', // fisc. bijtelling auto -> telt NIET mee (notioneel)
  'OVERIG',
] as const
export type LoonstrookComponentCategorie = (typeof LOONSTROOK_COMPONENT_CATEGORIE)[number]

/** Eén regel van een loonstrook. Bedragen in euro's (geen euroteken/duizendscheiding). */
export interface LoonstrookComponent {
  categorie: LoonstrookComponentCategorie
  omschrijving: string
  aantal: number | null
  bedragPeriode: number
  bedragCumulatief: number | null
}

export const LoonstrookComponentSchema = z.object({
  categorie: z.enum(LOONSTROOK_COMPONENT_CATEGORIE),
  omschrijving: z.string().trim().max(200),
  aantal: z.number().finite().nullable(),
  bedragPeriode: z.number().finite(),
  bedragCumulatief: z.number().finite().nullable(),
})
export const LoonstrookComponentenSchema = z.array(LoonstrookComponentSchema)

/** De categorieen die als structureel variabel WGV-inkomen meetellen (NHG footnote-4). */
export const MEETELLENDE_WGV_CATEGORIEEN = [
  'OVERWERK',
  'ONREGELMATIGHEID',
  'REISUREN',
  'PROVISIE',
] as const

// ─── Label constants (NL-talig voor UI) ───────────────────────────────────────

// ─── batch-66 (FO-492) — automatische vrijgave na goedkeuring ────────────────
//
// Er bestaat GEEN status "goedgekeurd": een strook geldt als goedgekeurd zodra
// hij niet afgekeurd is en alle controle-afwijkingen zijn afgehandeld (plus de
// randvoorwaarden hieronder). De automaat plant hem dan in op het standaard-
// moment van de periode. Zie `loonstrookNietGoedgekeurdReden` in @streep/utils.

/** Wat de automaat doet met een goedgekeurde strook waarvan het moment al voorbij is. */
export const LOONSTROOK_VERSTREKEN_MOMENT_GEDRAG = [
  'DIRECT_KLAARZETTEN',
  'EERSTVOLGEND_HEEL_UUR',
] as const
export type LoonstrookVerstrekenMomentGedrag = (typeof LOONSTROOK_VERSTREKEN_MOMENT_GEDRAG)[number]

export const LOONSTROOK_VERSTREKEN_MOMENT_GEDRAG_LABELS: Record<
  LoonstrookVerstrekenMomentGedrag,
  string
> = {
  DIRECT_KLAARZETTEN: 'Direct klaarzetten',
  EERSTVOLGEND_HEEL_UUR: 'Plannen op het eerstvolgende hele uur',
}

/**
 * Waarom een strook (nog) niet als goedgekeurd geldt. Eén reden: de eerste die
 * opgaat, in de volgorde waarin een controleur het oplost.
 */
export const LOONSTROOK_NIET_GOEDGEKEURD_REDEN = [
  'JAAROPGAVE',
  'AFGEKEURD',
  'NIET_GEKOPPELD',
  'KOPPELING_BEVESTIGEN',
  'RUN_KEUZE_OPEN',
  'NIET_GECONTROLEERD',
  'AFWIJKING_OPEN',
  'AFWIJKING_DOORGEGEVEN',
] as const
export type LoonstrookNietGoedgekeurdReden = (typeof LOONSTROOK_NIET_GOEDGEKEURD_REDEN)[number]

/** De volledige uitleg bij één strook (strook-detail). */
export const LOONSTROOK_NIET_GOEDGEKEURD_REDEN_LABELS: Record<
  LoonstrookNietGoedgekeurdReden,
  string
> = {
  JAAROPGAVE: 'Een jaaropgave wordt niet automatisch ingepland; zet hem zelf klaar.',
  AFGEKEURD: 'Afgekeurd: wacht op de gecorrigeerde strook van de salarisverwerker.',
  NIET_GEKOPPELD: 'Nog niet aan een medewerker gekoppeld.',
  KOPPELING_BEVESTIGEN:
    'De koppeling is een voorstel van het systeem. Bevestig de medewerker; daarna komt de strook automatisch vrij. Zelf inplannen kan ook.',
  RUN_KEUZE_OPEN: 'De keuze tussen extra run en correctie staat nog open.',
  NIET_GECONTROLEERD:
    'De controle heeft deze versie van de strook nog niet beoordeeld. Draai de controle opnieuw, of plan de strook zelf in.',
  AFWIJKING_OPEN: 'Er staat nog een controle-afwijking open.',
  AFWIJKING_DOORGEGEVEN:
    'Een afwijking is doorgegeven aan de salarisverwerker. Deze strook komt daarom niet vanzelf vrij: plan hem zelf in zodra hij klopt.',
}

/** Korte vorm voor de uitsplitsing in het planningsblok ("3 koppeling bevestigen"). */
export const LOONSTROOK_NIET_GOEDGEKEURD_REDEN_KORT: Record<
  LoonstrookNietGoedgekeurdReden,
  string
> = {
  JAAROPGAVE: 'jaaropgave',
  AFGEKEURD: 'afgekeurd',
  NIET_GEKOPPELD: 'niet gekoppeld',
  KOPPELING_BEVESTIGEN: 'koppeling bevestigen',
  RUN_KEUZE_OPEN: 'run-keuze open',
  NIET_GECONTROLEERD: 'nog niet gecontroleerd',
  AFWIJKING_OPEN: 'afwijking open',
  AFWIJKING_DOORGEGEVEN: 'doorgegeven aan de salarisverwerker',
}

/**
 * Filter op de stand voor de automatische vrijgave (loonstroken-overzicht). De
 * twee waarden zijn precies de tegels "nog te controleren" en "goedgekeurd" van
 * het planningsblok, zodat een tegel doorlinkt naar de stroken die hij telt.
 */
export const LOONSTROOK_VRIJGAVE_FILTERS = ['NOG_TE_CONTROLEREN', 'GOEDGEKEURD'] as const
export type LoonstrookVrijgaveFilter = (typeof LOONSTROOK_VRIJGAVE_FILTERS)[number]

/** De stand van één strook voor de automatische vrijgave (strook-detail). */
export interface LoonstrookVrijgaveStandDTO {
  /** Geldt de strook als goedgekeurd? */
  goedgekeurd: boolean
  /** Waarom niet (null wanneer goedgekeurd). */
  reden: LoonstrookNietGoedgekeurdReden | null
  redenLabel: string | null
  /**
   * Een mens heeft de geplande vrijgave geannuleerd of de strook na het
   * klaarzetten ingetrokken: de automaat plant deze strook niet opnieuw in, ook
   * niet als hij goedgekeurd is.
   */
  handmatigUitPlanning: boolean
  /**
   * De strook staat gepland en die planning volgt de goedkeuring (de automaat
   * plande hem): komt er een afwijking of een nieuwe versie bij, dan vervalt de
   * planning. `false` bij een strook die een mens zelf inplande: die komt op
   * het moment vrij, wat de controle daarna ook vindt.
   */
  planningVolgtGoedkeuring: boolean
}

export const LOONSTROOK_SOORT_LABELS: Record<LoonstrookSoort, string> = {
  LOONSTROOK: 'Loonstrook',
  JAAROPGAVE: 'Jaaropgave',
}

export const LOONSTROOK_STATUS_LABELS: Record<LoonstrookStatus, string> = {
  CONCEPT:     'Concept',
  GEPLAND:     'Gepland',
  KLAARGEZET:  'Klaargezet',
  INGETROKKEN: 'Ingetrokken',
  AFGEKEURD:   'Wacht op correctie salarisverwerker',
}

export const LOONSTROOK_RUN_TYPE_LABELS: Record<LoonstrookRunType, string> = {
  HOOFDRUN:  'Hoofdrun',
  EXTRA_RUN: 'Extra run',
}

export const LOONSTROOK_RUN_KEUZE_LABELS: Record<LoonstrookRunKeuze, string> = {
  TOEVOEGEN: 'Extra run (toevoegen naast de bestaande)',
  CORRECTIE: 'Correctie - vervangt de bestaande strook',
}

export const LOONSTROOK_BEVINDING_CATEGORIE_LABELS: Record<
  LoonstrookBevindingCategorie,
  string
> = {
  UREN:             'Uren',
  TOESLAG:          'Toeslag',
  VERGOEDING:       'Vergoeding',
  INHOUDING:        'Inhouding',
  PERSOONSGEGEVENS: 'Persoonsgegevens',
  OVERIG:           'Overig',
}

export const LOONSTROOK_VERZOEK_BESLUIT_LABELS: Record<LoonstrookVerzoekBesluit, string> = {
  // Of het ook doorgezet is, staat los hiervan op `doorgezetOp`.
  GOEDGEKEURD: 'Goedgekeurd',
  AFGEWEZEN:   'Afgewezen',
}

export const LOONSTROOK_MATCH_STATUS_LABELS: Record<LoonstrookMatchStatus, string> = {
  ONGEMATCHT:     'Ongematcht',
  AI_VOORGESTELD: 'AI-voorstel',
  BEVESTIGD:      'Bevestigd',
  HANDMATIG:      'Handmatig',
}

// ─── Zod-schemas voor request bodies ──────────────────────────────────────────

const jaarSchema = z.coerce.number().int().min(2000).max(2100)
const maandSchema = z.coerce.number().int().min(1).max(12)
const periodeNummerSchema = z.coerce.number().int().min(1).max(13)
/**
 * batch-56 (FO-422) — run binnen een periode. Bovengrens 20: ruim boven elke
 * realistische nabetalings-/correctierun, en een harde rem op een tikfout die
 * per ongeluk run 9999 zou aanmaken.
 */
const runNummerSchema = z.coerce.number().int().min(1).max(20)
/**
 * batch-61 loonstrook-dedupe (FO-456) — reden bij "toch als extra run" (omzeilt
 * de inhoud-dedupe bewust). Minimaal een paar woorden: een lege of eenletterige
 * reden is geen reden.
 */
const dedupeOverrideRedenSchema = z.string().trim().min(5).max(500).nullable().optional()
/**
 * batch-56 (FO-417) — e-mailadres van de salarisverwerker. Getrimd + lowercase,
 * zodat een geplakt adres met spaties of hoofdletters niet als een ander adres
 * in de instelling belandt.
 */
const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .email('Ongeldig e-mailadres')
  .max(200)

/**
 * Velden bij POST /payroll/loonstroken/upload. De PDF zelf komt via multipart
 * (apart gevalideerd op mime application/pdf); deze velden komen als
 * form-data-tekstvelden mee. `maand`/`periodeNummer`/`employeeId` zijn nullable
 * (een ongematchte upload kent nog geen medewerker).
 */
export const loonstrookUploadSchema = z
  .object({
    soort: z.enum(LOONSTROOK_SOORT),
    jaar: jaarSchema,
    periodeType: z.enum(LOONSTROOK_PERIODE_TYPE).default('MONTH'),
    maand: maandSchema.nullable().optional(),
    periodeNummer: periodeNummerSchema.nullable().optional(),
    employeeId: idParam('employeeId').nullable().optional(),
    /**
     * batch-56 (FO-422 §3.5) — wat moet er gebeuren als deze medewerker al een
     * strook voor deze periode heeft: `TOEVOEGEN` (default; extra run ernaast)
     * of `CORRECTIE` (nieuwe versie van de bestaande run). Bestaat er nog niets,
     * dan is de keuze betekenisloos en wordt hij genegeerd.
     */
    runKeuze: z.enum(LOONSTROOK_RUN_KEUZE).default('TOEVOEGEN'),
    /**
     * Optioneel expliciet run-nummer (1..20) bij `TOEVOEGEN`/`CORRECTIE`. Leeg =
     * de server bepaalt het (eerstvolgende vrije run bij toevoegen, de laatste
     * bestaande run bij correctie).
     */
    runNummer: runNummerSchema.nullable().optional(),
    /**
     * batch-61 loonstrook-dedupe (FO-456 §5.1) — "toch als extra run". De
     * inhoud-dedupe slaat een strook met dezelfde inhoud (vingerafdruk) over,
     * ook als de bytes verschillen. Wil de controleur hem bewust tóch als extra
     * run naast de bestaande zetten (twee legitieme runs met identieke
     * bedragen zonder run-aanduiding), dan geeft hij hier de reden. Een exact
     * identiek bestand (bytes) blijft altijd een dubbel.
     */
    dedupeOverrideReden: dedupeOverrideRedenSchema,
  })
  .superRefine((val, ctx) => {
    // Periode-consistentie: een loonstrook heeft een periode binnen het jaar;
    // een jaaropgave dekt het hele jaar (geen maand/periodeNummer nodig).
    if (val.soort === 'LOONSTROOK') {
      if (val.periodeType === 'MONTH' && val.maand == null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['maand'],
          message: 'maand is verplicht voor een maandloonstrook',
        })
      }
      if (val.periodeType === 'FOUR_WEEK' && val.periodeNummer == null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['periodeNummer'],
          message: 'periodeNummer is verplicht voor een 4-wekenloonstrook',
        })
      }
    }
  })

/** Body bij PATCH /payroll/loonstroken/:id/koppeling (handmatig koppelen/corrigeren). */
export const loonstrookKoppelSchema = z.object({
  employeeId: idParam('employeeId'),
})

/** Query bij GET /payroll/loonstroken (lijst + filters + paginatie). */
export const loonstrookListFilterSchema = z.object({
  jaar: jaarSchema.optional(),
  maand: maandSchema.optional(),
  periodeNummer: periodeNummerSchema.optional(),
  soort: z.enum(LOONSTROOK_SOORT).optional(),
  status: z.enum(LOONSTROOK_STATUS).optional(),
  matchStatus: z.enum(LOONSTROOK_MATCH_STATUS).optional(),
  employeeId: idParam('employeeId').optional(),
  // BV-filter: scope op loonstroken van medewerkers binnen deze entiteit (BV).
  // Een ongematchte strook heeft geen medewerker en valt dus buiten een
  // BV-selectie — net zoals bij het medewerker-filter.
  bvId: idParam('bvId').optional(),
  importBatchId: idParam('importBatchId').optional(),
  /**
   * batch-56 (FO-422) — alleen de stroken met een openstaande run-keuze. Zonder
   * dit filter is die werkvoorraad alleen te vinden door de hele lijst af te
   * scrollen op de badge, en dat is precies hoe zo'n stap blijft liggen.
   * Query-string, dus een literal-union i.p.v. `z.coerce.boolean()` (die maakt
   * van de string "false" een `true`).
   */
  runKeuzeVereist: z
    .union([z.literal('true'), z.literal('false')])
    .optional()
    .transform((v) => (v === undefined ? undefined : v === 'true')),
  /**
   * batch-66 (FO-492) — de stroken achter een tegel van het planningsblok:
   * "nog te controleren" (niet goedgekeurd: concept of afgekeurd) of
   * "goedgekeurd" (concept, wacht op planning). Server-side dezelfde where als
   * de tellingen, dus de lijst toont exact wat de tegel telt.
   */
  vrijgave: z.enum(LOONSTROOK_VRIJGAVE_FILTERS).optional(),
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().max(100).default(25),
})

/**
 * Query bij GET /payroll/loonstroken/export (bulk-ZIP + overzicht-CSV). Zelfde
 * filter-vorm als de lijst, maar zonder paginatie/match-status/batch: een export
 * bestrijkt een hele selectie (typisch een periode of een medewerker-jaar), niet
 * één pagina. Geen `page`/`pageSize` — de route begrenst zelf op een max-aantal.
 */
export const loonstrookExportFilterSchema = z.object({
  jaar: jaarSchema.optional(),
  maand: maandSchema.optional(),
  periodeNummer: periodeNummerSchema.optional(),
  soort: z.enum(LOONSTROOK_SOORT).optional(),
  status: z.enum(LOONSTROOK_STATUS).optional(),
  employeeId: idParam('employeeId').optional(),
  bvId: idParam('bvId').optional(),
})

/**
 * Query bij GET /payroll/loonstroken/jaaroverzicht (backoffice, gated): bruto per
 * maand voor één medewerker + jaar (grafiek). `employeeId` verplicht; de portal-
 * variant (`/loonstroken/mijn/jaaroverzicht`) leidt de medewerker server-side af.
 */
export const loonstrookJaaroverzichtFilterSchema = z.object({
  employeeId: idParam('employeeId'),
  jaar: jaarSchema,
})

/** Query bij GET /loonstroken/mijn/jaaroverzicht (portal, eigen): alleen jaar. */
export const mijnLoonstrookJaaroverzichtFilterSchema = z.object({
  jaar: jaarSchema,
})

// ─── Inferred input-types ─────────────────────────────────────────────────────

export type LoonstrookUploadInput = z.infer<typeof loonstrookUploadSchema>
export type LoonstrookKoppelInput = z.infer<typeof loonstrookKoppelSchema>
export type LoonstrookListFilter = z.infer<typeof loonstrookListFilterSchema>
export type LoonstrookExportFilter = z.infer<typeof loonstrookExportFilterSchema>

// ─── DTOs voor API responses ──────────────────────────────────────────────────

/**
 * Loonbedragen van een strook (batch-9 #28), uit de PDF-extractie. Decimal in de
 * DB → number in de DTO (afronden/format in de UI). Null = niet beschikbaar
 * (geen AI-key / niet op de strook). Achter de afscherming (zie header). NOOIT
 * BSN.
 */
export interface LoonstrookBedragenDTO {
  brutoLoon: number | null
  nettoLoon: number | null
  bijtellingNetto: number | null
  bijtellingBruto: number | null
  opgebouwdVakantiegeld: number | null
  /** Herkomst, bv. 'PDF_AI'; null wanneer niet beschikbaar. */
  bron: string | null
}

/**
 * Uren-breakdown uit STREEP's eigen verloning (`PayrollExportRegel`, read-only)
 * voor de periode van de loonstrook. Alle waarden number (Decimal in de DB).
 * `null` op het detail wanneer er voor die medewerker+periode geen
 * payroll-export-regel is. Toeslagen zijn BEDRAGEN (€) zoals STREEP ze verloont;
 * uren zijn uren.
 */
export interface LoonstrookUrenDTO {
  /** Betaalde (bruto) uren. */
  betaaldeUren: number
  overuren: number
  /** Betaalde reisuren. */
  reisuren: number
  reisKm: number
  ploegtoeslagBedrag: number
  zaterdagToeslagBedrag: number
  zondagToeslagBedrag: number
  feestdagToeslagBedrag: number
  overigeToeslagBedrag: number
  /** STREEP-verloning bruto-totaal van de periode (cross-check tegen de strook). */
  brutoTotaal: number
}

/**
 * Eén rij in het loonstroken-overzicht (backoffice). Bevat sinds #28 bruto/netto
 * (achter de afscherming) maar GEEN BSN.
 */
export interface LoonstrookListRowDTO {
  id: string
  employeeId: string | null
  employeeNaam: string | null
  soort: LoonstrookSoort
  jaar: number
  maand: number | null
  periodeNummer: number | null
  periodeType: LoonstrookPeriodeType
  status: LoonstrookStatus
  matchStatus: LoonstrookMatchStatus
  /** AI match-confidence (0.0000-1.0000), of null bij handmatig/ongematcht. */
  matchConfidence: number | null
  /** ISO datetime van upload. */
  geuploadOp: string
  /** ISO datetime van klaarzetten, of null. */
  klaargezetOp: string | null
  /** ISO datetime van de geplande vrijgave (status GEPLAND), of null. */
  geplandeVrijgaveOp: string | null
  /** ISO datetime waarop de medewerker de strook voor het eerst opende, of null. */
  gezienDoorMedewerkerOp: string | null
  /** FK naar het onderliggende Document (voor download via /documenten). */
  documentId: string
  /** Brutoloon uit de PDF (#28), of null. Achter de afscherming. */
  brutoLoon: number | null
  /** Nettoloon uit de PDF (#28), of null. Achter de afscherming. */
  nettoLoon: number | null
  /** batch-56 (FO-422) — HOOFDRUN (run 1) of EXTRA_RUN (run 2..n). */
  runType: LoonstrookRunType
  /** batch-56 (FO-422) — run-nummer binnen medewerker + periode (1..n). */
  runNummer: number
  /**
   * batch-56 (FO-422) — kant-en-klaar run-label voor de UI, of null bij run 1
   * (dan is er niets extra's te melden). Bv. "run 2 (extra)".
   */
  runLabel: string | null
  /**
   * batch-56 (FO-422) — deze strook kwam naast een bestaande strook van dezelfde
   * periode binnen ZONDER leesbare run-aanduiding en staat nu als extra run
   * (default toevoegen). De controleur bevestigt dat of maakt er een correctie
   * van. Zolang dit true is, is de plaatsing nog niet bevestigd.
   */
  runKeuzeVereist: boolean
}

/**
 * Eén maand in het jaaroverzicht-grafiek (#28 Deel 2): bruto-totaal van die
 * maand uit STREEP's verloning (`PayrollExportRegel.brutoTotaal`). 0 wanneer er
 * geen verloningsregel voor die maand is.
 */
export interface LoonstrookJaarMaandDTO {
  /** 1-12. */
  maand: number
  brutoTotaal: number
}

/** Jaaroverzicht: bruto per maand voor een medewerker + jaar (grafiek-bron). */
export interface LoonstrookJaaroverzichtDTO {
  jaar: number
  maanden: LoonstrookJaarMaandDTO[]
}

/** KPI-blok boven het loonstroken-overzicht. */
export interface LoonstrookKpiDTO {
  /** Aantal nog te koppelen (ongematchte) stroken. */
  teKoppelen: number
  /** Aantal concept-stroken (gekoppeld, nog niet klaargezet/gepland). */
  concept: number
  /** Aantal geplande stroken (wachten op het geplande vrijgave-moment). */
  gepland: number
  /** Aantal klaargezet binnen de huidige filter-periode. */
  klaargezetDezePeriode: number
  /** Aantal klaargezette stroken die de medewerker al heeft gezien. */
  gezienDoorMedewerker: number
  /**
   * batch-56 (FO-422) — aantal stroken waarvoor de run-plaatsing nog bevestigd
   * moet worden (extra run of toch een correctie). Server-side geteld over de
   * VOLLEDIGE filterset, niet over de geladen pagina.
   */
  runKeuzeOpen: number
}

/** Laatste documentversie van een loonstrook (voor preview/download-link). */
export interface LoonstrookDocumentVersieDTO {
  versionNr: number
  fileName: string
  fileSize: number
  mimeType: string
}

/** Eén regel in de loonstrook-tijdlijn (samengevoegd uit audit + status-tijdstippen). */
export interface LoonstrookTijdlijnItemDTO {
  actie: string
  actor: string | null
  /** Display-naam van de actor (User.name ?? email), indien resolvebaar. */
  actorNaam: string | null
  createdAt: string
  toelichting: string | null
}

/** Volledig detail van één loonstrook. Geen loonbedragen, geen BSN. */
export interface LoonstrookDetailDTO extends LoonstrookListRowDTO {
  /** Sleutel-NAAM waarop gematcht is ("handmatig"|"employeeNummer"|"bsn"|"naam-fuzzy"), nooit de waarde. */
  matchSleutel: string | null
  matchToelichting: string | null
  /** Actor (userId) die de strook klaarzette, of null. */
  klaargezetDoor: string | null
  /** Display-naam van de klaarzet-actor. */
  klaargezetDoorNaam: string | null
  /** Actor (userId) die de strook plande; null = automatisch ingepland (systeem). */
  geplandDoor: string | null
  /** Display-naam van de plan-actor, of null (bij automatisch ingepland). */
  geplandDoorNaam: string | null
  /** ISO datetime waarop de planning is gezet, of null. */
  geplandOp: string | null
  /** Actor (userId) die de strook introk, of null. */
  ingetrokkenDoor: string | null
  /** Display-naam van de intrek-actor. */
  ingetrokkenDoorNaam: string | null
  ingetrokkenOp: string | null
  geuploadDoor: string
  geuploadDoorNaam: string | null
  /** Laatste documentversie voor preview/download. */
  laatsteVersie: LoonstrookDocumentVersieDTO | null
  /** Gecombineerde tijdlijn (document-audit + status-mijlpalen), nieuw → oud. */
  tijdlijn: LoonstrookTijdlijnItemDTO[]
  /** Bedragen uit de PDF (#28); null wanneer niet geëxtraheerd. */
  bedragen: LoonstrookBedragenDTO | null
  /** Uren uit STREEP's verloning (#28); null wanneer geen payroll-regel. */
  uren: LoonstrookUrenDTO | null
  /** Correctieverzoeken/vragen bij deze strook (#28 Deel 4), nieuw → oud. */
  verzoeken: LoonstrookVerzoekDTO[]
  /** Herkomst-batch (bulk-import), of null bij een losse upload/mail-ingest. */
  importBatchId: string | null
  /** batch-56 (FO-417) — afkeur-rondes bij deze strook, nieuw → oud. */
  afkeuringen: LoonstrookAfkeuringDTO[]
  /**
   * batch-56 (FO-422) — de ANDERE runs van dezelfde medewerker + periode, zodat
   * de controleur de stroken naast elkaar ziet bij een openstaande run-keuze.
   * Leeg wanneer dit de enige run is.
   */
  andereRuns: LoonstrookRunSamenvattingDTO[]
  /**
   * batch-59 loonstrook-controles (FO-433) — de voertuig-/bijtelling-afwijkingen
   * die de controle op DEZE strook vond, zodat de controleur ze naast de strook
   * ziet en niet eerst het run-rapport hoeft op te zoeken. Leeg wanneer er niets
   * gevonden is (of de controle nog niet gedraaid heeft).
   */
  controleAfwijkingen: LoonstrookControleAfwijkingDTO[]
  /** Actor (userId) die de strook afkeurde, of null. */
  afgekeurdDoor: string | null
  /** Display-naam van de afkeur-actor. */
  afgekeurdDoorNaam: string | null
  afgekeurdOp: string | null
  /**
   * batch-61 loonstrook-dedupe (FO-456 §3.6) — hoe vaak deze strook is
   * aangeleverd (1 = alleen de oorspronkelijke binnenkomst) en wanneer voor het
   * laatst (ISO), voor "aangeleverd {n}x (laatst {datum})". De losse
   * aanleveringen (incl. de dubbelen) staan in `aanleveringen`, nieuw -> oud.
   */
  aangeleverdAantal: number
  laatstAangeleverdOp: string | null
  /** Reden waarmee de controleur de inhoud-dedupe bewust omzeilde, of null. */
  dedupeOverrideReden: string | null
  aanleveringen: LoonstrookAanleveringDTO[]
  /** batch-66 (FO-492) — waar deze strook staat voor de automatische vrijgave. */
  vrijgave: LoonstrookVrijgaveStandDTO
}

/**
 * batch-56 (FO-422) — compacte samenvatting van een andere run in dezelfde
 * periode (voor de naast-elkaar-weergave bij een run-keuze). Bewust smal: alleen
 * wat nodig is om de twee stroken te onderscheiden.
 */
export interface LoonstrookRunSamenvattingDTO {
  id: string
  runType: LoonstrookRunType
  runNummer: number
  runLabel: string | null
  runAanduiding: string | null
  status: LoonstrookStatus
  documentId: string
  /** Laatste documentversie-nummer (voor de preview-/download-link). */
  versionNr: number | null
  geuploadOp: string
  brutoLoon: number | null
  nettoLoon: number | null
}

// ─── batch-56 loonstroken (FO-417) — afkeuren met bevindingen ─────────────────

/** Eén bevinding binnen een afkeur-ronde: wat klopt niet + wat moet er anders. */
export interface LoonstrookBevindingDTO {
  id: string
  categorie: LoonstrookBevindingCategorie
  watKloptNiet: string
  gewensteAanpassing: string
  volgorde: number
}

/**
 * Eén afkeur-ronde op een loonstrook, met het mailspoor naar de
 * salarisverwerker. `mailStatus` komt LIVE uit de uitbox (`OutgoingEmail`), niet
 * uit een gekopieerd veld - één waarheid over bezorging (FO-146).
 */
export interface LoonstrookAfkeuringDTO {
  id: string
  afgekeurdDoorUserId: string
  afgekeurdDoorNaam: string | null
  afgekeurdOp: string
  bevindingen: LoonstrookBevindingDTO[]
  /** Adressen zoals ze op verzendmoment in de instelling stonden. */
  mailNaarTo: string[]
  mailNaarCc: string[]
  mailVerzondenOp: string | null
  /** OutgoingEmail-id (uitbox-referentie), of null wanneer er niets verstuurd is. */
  mailOutgoingEmailId: string | null
  /** Live status uit de uitbox (QUEUED/SENT/BOUNCED/FAILED/...), of null. */
  mailStatus: string | null
  /** Is de mail niet bezorgd? Dan toont de UI de herverzend-actie (FO-146). */
  mailMislukt: boolean
  herverzondenOp: string | null
  herverzondenDoorNaam: string | null
  afgerondOp: string | null
  afgerondReden: string | null
}

// ─── Verwijderen (backoffice; portal blijft read-only) ────────────────────────
//
// Soft-delete van een geïmporteerde loonstrook + het onderliggende Document.
// Alleen CONCEPT/GEPLAND/INGETROKKEN; een KLAARGEZETTE strook moet eerst
// ingetrokken worden (de medewerker kan hem al gezien hebben). Open verzoeken
// worden automatisch afgesloten - de UI toont dat vooraf in de bevestiging.

/** Response van DELETE /payroll/loonstroken/:id. */
export interface LoonstrookVerwijderResponse {
  verwijderd: true
  /** Aantal open verzoeken (OPEN/BEANTWOORD) dat automatisch is afgesloten. */
  verzoekenAfgesloten: number
}

/** Response van DELETE /payroll/loonstroken/import/:batchId (hele batch). */
export interface LoonstrookBatchVerwijderResponse {
  /** Aantal verwijderde loonstroken. */
  verwijderd: number
  /** Aantal KLAARGEZETTE stroken dat is overgeslagen (eerst intrekken). */
  overgeslagenKlaargezet: number
  /** Aantal open verzoeken dat over de verwijderde stroken is afgesloten. */
  verzoekenAfgesloten: number
  /** Is de batch-rij zelf mee verwijderd (alleen als er geen stroken overbleven)? */
  batchVerwijderd: boolean
}

// ─── Bulk-import (spec B, stap 3a): AI-analyse + match-cascade + commit ────────
//
// Twee stappen, gespiegeld op de factuur-bulk-import (spec 09 §7):
//   1. analyze: 1+ PDF's (multipart) → per rij een AI-extractie (periode/soort)
//      + een medewerker-match-suggestie. De staging-PDF wordt opgeslagen onder
//      een batch-staging-key; de match-cascade draait in-memory.
//   2. commit: de door de gebruiker gevalideerde rijen → Document + Loonstrook
//      (status CONCEPT). Staging-PDF verhuist naar de canonieke document-key.
//
// AVG: loonbedragen en BSN komen NOOIT in een analyze-respons of in
// `analyseResultaat`. De BSN wordt uitsluitend in-memory gebruikt voor de match
// (sleutel 'bsn') en daarna weggegooid — alleen de sleutel-NAAM blijft over.

/**
 * Sleutel-NAAM waarop de import-cascade matchte. Nooit de gevoelige waarde.
 * `nmbrsNummer` (FO-069) = exacte match op `Employee.nmbrsWerknemernummer`, het
 * expliciet beheerde Nmbrs-werknemernummer (dat structureel van `employeeNumber`
 * kan afwijken).
 */
export const LOONSTROOK_MATCH_SLEUTEL = [
  'employeeNummer',
  'nmbrsNummer',
  'bsn',
  'naam',
  'naam-fuzzy',
] as const
export type LoonstrookMatchSleutel = (typeof LOONSTROOK_MATCH_SLEUTEL)[number]

/**
 * FO-069 — niet-gevoelige reden per geanalyseerde/gecommitte rij: waaróm een
 * strook wel of niet (automatisch) aan een medewerker kon worden gekoppeld. Bevat
 * NOOIT loonbedragen of BSN; uitsluitend procesmatige categorieën zodat een
 * mislukte import diagnosticeerbaar en exporteerbaar is.
 */
export const LOONSTROOK_IMPORT_REDEN = [
  'GEKOPPELD',
  'GEEN_NUMMER_OP_STROOK',
  'NUMMER_ONBEKEND',
  'MEERDERE_NAAMGENOTEN',
  'EXTRACTIE_MISLUKT',
  'PDF_ONLEESBAAR',
  'GEEN_INZAGE_AI',
  // batch-56 (FO-422 T2) — exact hetzelfde document was al ingelezen
  // (bestands-/inhoud-hash gelijk). Geen tweede strook, geen tweede run.
  'DUPLICAAT',
  // batch-61 (FO-456) — zelfde INHOUD (vingerafdruk) als een bestaande strook,
  // ander bestand (opnieuw gegenereerde PDF). Overgeslagen met notitie op de
  // bestaande strook; geen tweede rij, geen run 2.
  'DUPLICAAT_INHOUD',
] as const
export type LoonstrookImportReden = (typeof LOONSTROOK_IMPORT_REDEN)[number]

export const LOONSTROOK_IMPORT_REDEN_LABELS: Record<LoonstrookImportReden, string> = {
  GEKOPPELD:             'Gekoppeld',
  GEEN_NUMMER_OP_STROOK: 'Geen personeelsnummer op de strook',
  NUMMER_ONBEKEND:       'Personeelsnummer onbekend in STREEP',
  MEERDERE_NAAMGENOTEN:  'Meerdere medewerkers met deze naam',
  EXTRACTIE_MISLUKT:     'Kon de strook niet uitlezen',
  PDF_ONLEESBAAR:        'PDF niet leesbaar',
  GEEN_INZAGE_AI:        'AI-herkenning niet beschikbaar',
  DUPLICAAT:             'Dit document was al ingelezen',
  DUPLICAAT_INHOUD:      'Deze strook was al ingelezen (zelfde inhoud, ander bestand); toch een extra run? Gebruik de losse upload met reden',
}

/**
 * Eén rij uit de analyze-stap. Draagt ALLEEN niet-gevoelige metadata: de
 * herkende periode/soort, de naam/het personeelsnummer zoals op de strook
 * (nodig voor de UI-bevestiging) en de match-suggestie. GEEN BSN, GEEN
 * loonbedragen. `bsnAanwezig` zegt enkel óf er een BSN op de strook stond.
 */
export interface LoonstrookAnalyzeRowDTO {
  /** Stabiele sleutel binnen deze batch (page-index of bestandsnaam-afgeleide). */
  rowKey: string
  /** Bestandsnaam van de (gesplitste) PDF, informatief voor de UI. */
  fileName: string
  /** Staging-storageKey van de gesplitste PDF (commit leest deze terug). */
  storageKey: string
  soort: LoonstrookSoort | null
  jaar: number | null
  periodeType: LoonstrookPeriodeType | null
  maand: number | null
  periodeNummer: number | null
  /** Medewerkernaam zoals herkend op de strook (geen DB-naam). */
  medewerkerNaam: string | null
  /** Personeelsnummer zoals herkend op de strook. */
  personeelsnummer: string | null
  /** Stond er een BSN op de strook? (Het BSN zelf wordt NOOIT teruggegeven.) */
  bsnAanwezig: boolean
  /**
   * FO-069 — niet-gevoelige reden waaróm deze rij wel/niet (automatisch) koppelde
   * (geen nummer / nummer onbekend / naamgenoten / extractie mislukt / geen
   * AI-inzage). Zichtbaar per rij in de review én in het eindscherm + foutrapport.
   */
  reden: LoonstrookImportReden
  /**
   * batch-56 (FO-422) — de leesbare run-/verloningsaanduiding zoals die op de
   * strook stond ("Verloningsrun 2"), of null wanneer er niets stond. Niet
   * gevoelig (geen bedrag/BSN).
   */
  runAanduiding: string | null
  /** Het run-nummer dat uit `runAanduiding` af te leiden was, of null. */
  runNummer: number | null
  /**
   * Heeft deze medewerker al een strook voor deze periode? Dan toont de wizard
   * de run-keuze (default toevoegen). Null bij een ongematchte rij.
   */
  bestaandeRuns: number | null
  match: {
    /** Voorgestelde medewerker, of null wanneer ongematcht. */
    employeeId: string | null
    /** Display-naam van de voorgestelde medewerker (DB), of null. */
    employeeNaam: string | null
    /** 0.0-1.0; null bij ongematcht. */
    confidence: number | null
    matchStatus: LoonstrookMatchStatus
    matchSleutel: LoonstrookMatchSleutel | null
    toelichting: string | null
  }
}

/** Response van POST /payroll/loonstroken/import/analyze. */
export interface LoonstrookAnalyzeResponse {
  batchId: string
  /** Was AI-extractie/-matching actief? (false zonder ANTHROPIC_API_KEY.) */
  aiEnabled: boolean
  rows: LoonstrookAnalyzeRowDTO[]
}

/**
 * Eén te committen rij. De client stuurt de (mogelijk gecorrigeerde)
 * periode/soort + de definitieve `employeeId` terug. `employeeId === null`
 * betekent: nog ongematcht → niet committen. `matchSleutel`/`matchConfidence`
 * zijn de waarden uit de analyze-stap zodat de provenance bewaard blijft.
 */
export const loonstrookCommitRowSchema = z
  .object({
    rowKey: z.string().min(1),
    storageKey: z.string().min(1),
    /** Bestandsnaam (informatief) zodat het commit-foutrapport het bestand kan noemen. */
    fileName: z.string().max(300).optional(),
    soort: z.enum(LOONSTROOK_SOORT),
    jaar: jaarSchema,
    periodeType: z.enum(LOONSTROOK_PERIODE_TYPE).default('MONTH'),
    maand: maandSchema.nullable().optional(),
    periodeNummer: periodeNummerSchema.nullable().optional(),
    employeeId: idParam('employeeId').nullable().optional(),
    matchStatus: z.enum(LOONSTROOK_MATCH_STATUS),
    matchSleutel: z.enum(LOONSTROOK_MATCH_SLEUTEL).nullable().optional(),
    matchConfidence: z.number().min(0).max(1).nullable().optional(),
    /**
     * batch-56 (FO-422) - run-plaatsing voor deze rij.
     *
     * `null`/weggelaten betekent NIET "toevoegen", maar "de controleur heeft
     * hier niets over beslist" - de wizard toont de vraag alleen wanneer er al
     * een strook voor die medewerker + periode staat. De strook komt dan alsnog
     * als extra run binnen (niets raakt kwijt), maar mét een openstaande
     * keuze-stap. Zou de client hier blind `TOEVOEGEN` sturen, dan telde dat als
     * een bewuste keuze en verviel het "beide stroken naast elkaar"-moment.
     */
    runKeuze: z.enum(LOONSTROOK_RUN_KEUZE).nullable().optional(),
    /** Expliciet run-nummer (van de strook zelf of door de controleur gekozen). */
    runNummer: runNummerSchema.nullable().optional(),
    /**
     * De leesbare run-aanduiding zoals de analyse hem op de strook las. Puur
     * provenance ("waarom staat dit op run 2?"); niet-gevoelig.
     */
    runAanduiding: z.string().trim().max(60).nullable().optional(),
    /** batch-61 (FO-456 §5.1) — "toch als extra run"-reden; zie loonstrookUploadSchema. */
    dedupeOverrideReden: dedupeOverrideRedenSchema,
  })
  .superRefine((val, ctx) => {
    // Periode-consistentie (zelfde regel als bij de individuele upload): een
    // maandloonstrook vereist `maand`, een 4-wekenloonstrook `periodeNummer`.
    if (val.soort === 'LOONSTROOK') {
      if (val.periodeType === 'MONTH' && val.maand == null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['maand'],
          message: 'maand is verplicht voor een maandloonstrook',
        })
      }
      if (val.periodeType === 'FOUR_WEEK' && val.periodeNummer == null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['periodeNummer'],
          message: 'periodeNummer is verplicht voor een 4-wekenloonstrook',
        })
      }
    }
  })

/** Body bij POST /payroll/loonstroken/import/commit. */
export const loonstrookCommitSchema = z.object({
  batchId: idParam('batchId'),
  rows: z.array(loonstrookCommitRowSchema).min(1),
})

export type LoonstrookCommitRowInput = z.infer<typeof loonstrookCommitRowSchema>
export type LoonstrookCommitInput = z.infer<typeof loonstrookCommitSchema>

/**
 * FO-069 — één overgeslagen import-rij bij commit, met niet-gevoelige reden. Geen
 * loonbedragen/BSN; uitsluitend bestandsnaam + procesmatige reden zodat een
 * mislukte batch deelbaar/exporteerbaar is.
 */
export interface LoonstrookImportOvergeslagenDTO {
  rowKey: string
  fileName: string | null
  reden: LoonstrookImportReden
}

/** Response van POST /payroll/loonstroken/import/commit. */
export interface LoonstrookCommitResponse {
  /** Aantal nieuw aangemaakte loonstroken (incl. nieuwe-versie-toevoegingen). */
  created: number
  /** Aantal rijen zonder employeeId die (bewust) niet gecommit zijn. */
  ongematcht: number
  /** Aantal rijen overgeslagen om een andere reden (bv. dedup of leesfout). */
  skipped: number
  /** FO-069 — per overgeslagen rij de niet-gevoelige reden (bestandsnaam + reden). */
  overgeslagen: LoonstrookImportOvergeslagenDTO[]
}

// ─── Klaarzetten / intrekken (spec B, stap 4a) ────────────────────────────────

/**
 * Optioneel gepland vrijgave-moment (UTC-ISO). Leeg = nu vrijgeven (huidig gedrag);
 * gezet = de strook(en) op dat moment plannen (status GEPLAND). De API valideert
 * dat het moment in de toekomst ligt. De frontend rekent de NL-wandklok naar een
 * UTC-ISO via `wallClockToUtcIso` (@streep/utils) vóór verzending.
 */
const vrijgaveOpSchema = z.string().datetime({ offset: true }).optional()

/**
 * batch-66 (FO-492) — de status die de gebruiker voor zich zag toen hij klikte.
 * Wijkt de werkelijke status af (de planning van de automaat verviel intussen,
 * iemand anders gaf de strook vrij), dan weigert de API met 409. Zonder dit zou
 * "wijzig moment" op een verouderd scherm een vervallen planning stil omzetten
 * in een strook die de gebruiker zelf inplant en die hoe dan ook vrijkomt.
 */
const verwachteStatusSchema = z.enum(LOONSTROOK_STATUS).optional()

/** Body bij POST /payroll/loonstroken/:id/klaarzetten (los; leeg = nu vrijgeven). */
export const loonstrookKlaarzetSchema = z.object({
  vrijgaveOp: vrijgaveOpSchema,
  verwachteStatus: verwachteStatusSchema,
})
export type LoonstrookKlaarzetInput = z.infer<typeof loonstrookKlaarzetSchema>

/** Body bij POST /payroll/loonstroken/klaarzetten (bulk). */
export const loonstrookBulkKlaarzetSchema = z.object({
  ids: z.array(idParam('id')).min(1).max(500),
  vrijgaveOp: vrijgaveOpSchema,
  /**
   * Per strook de status zoals de lijst hem toonde (id -> status). Een strook
   * waarvan de status intussen is gewijzigd wordt overgeslagen, met reden.
   */
  verwachteStatussen: z.record(z.string(), z.enum(LOONSTROOK_STATUS)).optional(),
})
export type LoonstrookBulkKlaarzetInput = z.infer<typeof loonstrookBulkKlaarzetSchema>

/** Eén overgeslagen rij bij bulk-klaarzetten, met reden. */
export interface LoonstrookOvergeslagenDTO {
  id: string
  reden: string
}

/** Response van POST /payroll/loonstroken/klaarzetten (bulk). */
export interface LoonstrookBulkKlaarzetResponse {
  /** Aantal loonstroken dat direct is vrijgegeven (klaargezet). */
  klaargezet: number
  /** Aantal loonstroken dat is ingepland voor een latere vrijgave (status GEPLAND). */
  gepland: number
  /** Rijen die niet klaargezet/gepland konden worden, met reden. */
  overgeslagen: LoonstrookOvergeslagenDTO[]
}

// ─── batch-66 (FO-492) — planning per periode ────────────────────────────────

/** De maand als 1-12; de query levert een string, de body een getal. */
const planningMaand = z.coerce.number().int().min(1).max(12)
const planningPeriodeNummer = z.coerce.number().int().min(1).max(13)

/**
 * Query bij GET /payroll/loonstroken/planning. Zonder periode kiest de API de
 * nieuwste periode waarvoor loonstroken bestaan. Een periode is een maand OF een
 * vierwekenperiode, nooit beide.
 */
export const loonstrookPlanningQuerySchema = z
  .object({
    jaar: z.coerce.number().int().min(2000).max(2100).optional(),
    maand: planningMaand.optional(),
    periodeNummer: planningPeriodeNummer.optional(),
  })
  .superRefine((v, ctx) => {
    if (v.maand !== undefined && v.periodeNummer !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['periodeNummer'],
        message: 'Kies een maand of een periodenummer, niet allebei.',
      })
    }
    if ((v.maand !== undefined || v.periodeNummer !== undefined) && v.jaar === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['jaar'],
        message: 'Kies ook een jaar.',
      })
    }
  })
export type LoonstrookPlanningQuery = z.infer<typeof loonstrookPlanningQuerySchema>

/**
 * Eén gepland vrijgavemoment binnen een periode, per run, met het aantal
 * stroken erop. Een periode heeft meestal één run; een nabetaling of correctie
 * (FO-422) staat als run 2..n naast de hoofdrun en kan een eigen moment hebben.
 */
export interface LoonstrookGeplandMomentDTO {
  /** Run-nummer binnen de periode (1 = hoofdrun). */
  runNummer: number
  /** UTC-ISO van het geplande vrijgavemoment. */
  vrijgaveOp: string
  aantal: number
  /**
   * Waarvan door een mens ZELF ingepland (vanuit concept): die komen op het
   * moment vrij, wat de controle daarna ook vindt. De rest plande de automaat
   * en volgt de goedkeuring, ook als iemand het moment verzette.
   */
  handmatig: number
}

/** Hoeveel stroken om één reden nog niet goedgekeurd zijn. */
export interface LoonstrookNietGoedgekeurdTellingDTO {
  reden: LoonstrookNietGoedgekeurdReden
  aantal: number
}

/**
 * Waar de loonstroken van één periode staan. De vijf tellingen vormen samen het
 * totaal (elke strook telt in precies één) en zijn server-side geteld over de
 * volledige periode, nooit over een geladen pagina.
 */
export interface LoonstrookPlanningOverzichtDTO {
  jaar: number
  maand: number | null
  periodeNummer: number | null
  /** "september 2026" of "periode 9 2026". */
  periodeLabel: string
  totaal: number
  /** Niet goedgekeurd: open afwijking, koppeling of run-keuze te bevestigen, of afgekeurd. */
  nogTeControleren: number
  /**
   * `nogTeControleren` uitgesplitst naar reden (alleen redenen met stroken),
   * in de volgorde waarin een controleur ze oplost. Elke strook telt bij precies
   * één reden: de som is `nogTeControleren`.
   */
  nogTeControlerenPerReden: LoonstrookNietGoedgekeurdTellingDTO[]
  /** Deel van `nogTeControleren` dat afgekeurd is en op een correctie wacht. */
  afgekeurd: number
  /** Goedgekeurd, maar (nog) niet gepland of klaargezet. */
  goedgekeurd: number
  /** Deel van `goedgekeurd` dat een mens uit de automatische planning haalde. */
  handmatigUitPlanning: number
  gepland: number
  /** De geplande momenten van deze periode per run, vroegste eerst. */
  geplandeMomenten: LoonstrookGeplandMomentDTO[]
  klaargezet: number
  ingetrokken: number
  /** Staat de automaat aan (instelling)? */
  autoVrijgaveActief: boolean
  /**
   * Het standaard vrijgavemoment van deze periode volgens de instellingen
   * (UTC-ISO), of null: automaat uit, geen standaard, of een vierwekenperiode.
   */
  standaardVrijgaveOp: string | null
}

/**
 * Body bij POST /payroll/loonstroken/planning/verzetten: verzet ALLE geplande
 * stroken van één periode naar een nieuw moment (toekomst; de API controleert
 * dat). Met `runNummer` alleen de geplande stroken van die run (FO-422); zonder
 * de hele periode. Het MOMENT telt daarna als door een mens gekozen; een planning
 * van de automaat blijft de goedkeuring volgen.
 */
export const loonstrookPlanningVerzetSchema = z
  .object({
    jaar: z.coerce.number().int().min(2000).max(2100),
    maand: planningMaand.nullable().optional(),
    periodeNummer: planningPeriodeNummer.nullable().optional(),
    runNummer: z.coerce.number().int().min(1).max(99).nullable().optional(),
    vrijgaveOp: z.string().datetime({ offset: true, message: 'Kies een geldige datum en tijd.' }),
  })
  .superRefine((v, ctx) => {
    const heeftMaand = v.maand != null
    const heeftPeriode = v.periodeNummer != null
    if (heeftMaand === heeftPeriode) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [heeftMaand ? 'periodeNummer' : 'maand'],
        message: 'Kies een maand of een periodenummer.',
      })
    }
  })
export type LoonstrookPlanningVerzetInput = z.infer<typeof loonstrookPlanningVerzetSchema>

/** Response van POST /payroll/loonstroken/planning/verzetten. */
export interface LoonstrookPlanningVerzetResponse {
  /** Aantal geplande stroken dat naar het nieuwe moment is verzet. */
  verzet: number
  /** Het nieuwe moment (UTC-ISO, echo). */
  vrijgaveOp: string
}

/**
 * batch-66 (FO-492) — wat de automaat deed binnen een reikwijdte (een strook,
 * een run, een periode). Staat in `meta.vrijgave` van de routes waar een mens
 * op een knop drukt en daarmee de goedkeuring van stroken kan veranderen
 * ("Controle herdraaien", een voertuig koppelen), zodat het scherm kan zeggen
 * dat er stroken zijn ingepland of direct beschikbaar gezet.
 */
export interface LoonstrookAutoPlanResultaatDTO {
  /** Aantal stroken dat op een moment is ingepland (status GEPLAND). */
  gepland: number
  /** Aantal stroken dat direct is klaargezet (het moment was al voorbij). */
  klaargezet: number
  /**
   * Aantal planningen van de automaat dat is vervallen (terug naar CONCEPT): de
   * strook gold niet meer als goedgekeurd, of de automaat staat uit.
   */
  teruggezet: number
}

// ─── Portal (medewerker) DTOs (spec B, stap 4a) ───────────────────────────────
//
// De medewerker ziet UITSLUITEND zijn EIGEN klaargezette loonstroken. Geen
// loonbedragen, geen BSN, geen match-/workflow-interne velden. De PDF-download
// loopt via het bestaande GET /documenten/:id/versies/:nr/download (waarvan de
// scope-check de eigenaar toelaat).

/** Eén rij in het medewerker-loonstroken-overzicht (portal). */
export interface MijnLoonstrookListRowDTO {
  id: string
  soort: LoonstrookSoort
  jaar: number
  maand: number | null
  periodeNummer: number | null
  periodeType: LoonstrookPeriodeType
  /** Altijd 'KLAARGEZET' (alleen klaargezette stroken zijn zichtbaar). */
  status: LoonstrookStatus
  /** ISO datetime van upload. */
  geuploadOp: string
  /** ISO datetime van klaarzetten. */
  klaargezetOp: string | null
  /** FK naar het onderliggende Document (voor download via /documenten). */
  documentId: string
  /** Laatste documentversie-nummer (voor de download-link). */
  versionNr: number | null
  /** Heeft de medewerker deze strook al geopend? */
  gezien: boolean
  /** Eigen brutoloon uit de PDF (#28), of null. */
  brutoLoon: number | null
  /** Eigen nettoloon uit de PDF (#28), of null. */
  nettoLoon: number | null
  /** batch-56 (FO-422) — HOOFDRUN (run 1) of EXTRA_RUN (run 2..n). */
  runType: LoonstrookRunType
  /** batch-56 (FO-422) — run-nummer binnen de periode (1..n). */
  runNummer: number
  /** batch-56 (FO-422) — run-label voor de UI, of null bij run 1. */
  runLabel: string | null
}

/**
 * Detail van één eigen loonstrook (portal). Bevat sinds #28 de eigen bedragen +
 * uren (eigen salaris van de medewerker; geen BSN).
 */
export interface MijnLoonstrookDetailDTO extends MijnLoonstrookListRowDTO {
  /** Bestandsnaam van de laatste versie (voor de download-knop). */
  fileName: string | null
  /** MIME-type van de laatste versie (altijd application/pdf). */
  mimeType: string | null
  /** Bestandsgrootte in bytes van de laatste versie. */
  fileSize: number | null
  /** Eigen bedragen uit de PDF (#28); null wanneer niet geëxtraheerd. */
  bedragen: LoonstrookBedragenDTO | null
  /** Eigen uren uit STREEP's verloning (#28); null wanneer geen payroll-regel. */
  uren: LoonstrookUrenDTO | null
  /** Eigen correctieverzoeken/vragen bij deze strook (#28 Deel 4), nieuw → oud. */
  verzoeken: LoonstrookVerzoekDTO[]
}

// ─── Instellingen: upload-herinnering (spec B, stap 6) ────────────────────────
//
// Singleton-configuratie voor de 26e-van-de-maand upload-herinnering. De
// `verantwoordelijkeUserIds` zijn de expliciete ontvangers; is die lijst leeg,
// dan valt de notificatie-resolver terug op iedereen met de rol SALARIS (er
// staat dus NOOIT een hardcoded persoon in code). De UI laat een medewerker
// kiezen (EmployeeMultiPicker) en mapt die naar hun userId bij opslaan.

/**
 * Body bij PUT /payroll/loonstroken/instellingen. `verantwoordelijkeUserIds`
 * zijn `User`-id's (de UI mapt de gekozen medewerkers → hun userId vóór de
 * call). `herinnerDag` 1-28 (geen 29-31 zodat de reminder elke maand bestaat),
 * `herinnerUur` 0-23 (Europe/Amsterdam).
 */
export const loonstrookInstellingenUpdateSchema = z
  .object({
    herinneringActief: z.boolean(),
    herinnerDag: z.coerce.number().int().min(1).max(28),
    herinnerUur: z.coerce.number().int().min(0).max(23),
    verantwoordelijkeUserIds: z.array(idParam('userId')).max(50),
    // Geplande vrijgave: opt-in auto-planning + bedrijfsbrede standaard (NL-tijd).
    // Optioneel zodat een (deel-)update zonder deze velden blijft werken; de
    // backoffice-form stuurt ze altijd mee, een weggelaten veld blijft ongewijzigd
    // (Prisma negeert `undefined`) resp. valt bij create terug op de schema-default.
    autoVrijgaveActief: z.boolean().optional(),
    geplandeVrijgaveStandaardDag: z.coerce.number().int().min(1).max(28).nullable().optional(),
    geplandeVrijgaveStandaardUur: z.coerce.number().int().min(0).max(23).nullable().optional(),
    // batch-66 (FO-492) — wat de automaat doet met een goedgekeurde strook
    // waarvan het vrijgavemoment al voorbij is. Optioneel: weggelaten = ongewijzigd.
    verstrekenMomentGedrag: z
      .enum(LOONSTROOK_VERSTREKEN_MOMENT_GEDRAG, 'Kies wat er met een verstreken moment gebeurt.')
      .optional(),
    // ── batch-56 (FO-417) — salarisverwerker + beoordelaars ─────────────────
    // Alle velden optioneel zodat een bestaande (deel-)update zonder deze velden
    // blijft werken; een weggelaten veld blijft ongewijzigd.
    salarisverwerkerNaam: z.string().trim().max(120).nullable().optional(),
    salarisverwerkerTo: z.array(emailSchema).max(10).optional(),
    salarisverwerkerCc: z.array(emailSchema).max(10).optional(),
    verzoekBeoordelaarUserIds: z.array(idParam('userId')).max(50).optional(),
  })
  .superRefine((v, ctx) => {
    // Een CC zonder TO betekent dat de mail niemand als geadresseerde heeft:
    // dat is geen half-ingevulde instelling maar een stille bezorgfout.
    if ((v.salarisverwerkerCc?.length ?? 0) > 0 && (v.salarisverwerkerTo?.length ?? 0) === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['salarisverwerkerTo'],
        message: 'Vul minimaal één hoofdontvanger in zolang er een CC staat',
      })
    }
    // Auto-planning vereist een ingesteld standaard-moment (anders weet de cron geen
    // dag/uur). Dag/uur los instellen mag wel zolang auto-planning uit staat.
    if (v.autoVrijgaveActief && v.geplandeVrijgaveStandaardDag == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['geplandeVrijgaveStandaardDag'],
        message: 'Verplicht zolang auto-planning aan staat',
      })
    }
    if (v.autoVrijgaveActief && v.geplandeVrijgaveStandaardUur == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['geplandeVrijgaveStandaardUur'],
        message: 'Verplicht zolang auto-planning aan staat',
      })
    }
  })
export type LoonstrookInstellingenUpdateInput = z.infer<
  typeof loonstrookInstellingenUpdateSchema
>

/**
 * Eén geresolveerde verantwoordelijke voor de picker (display + mapping). Bevat
 * de `userId` (de opgeslagen waarde), de bijbehorende `employeeId` (waarmee de
 * EmployeeMultiPicker pre-selecteert) en de display-naam. `employeeId` kan null
 * zijn als de user geen gekoppelde medewerker (meer) heeft.
 */
export interface LoonstrookVerantwoordelijkeDTO {
  userId: string
  employeeId: string | null
  naam: string
}

/** Response van GET/PUT /payroll/loonstroken/instellingen. */
export interface LoonstrookInstellingenDTO {
  herinneringActief: boolean
  herinnerDag: number
  herinnerUur: number
  /** Opgeslagen ontvanger-userIds (rauw, voor round-trip). */
  verantwoordelijkeUserIds: string[]
  /** Dezelfde ontvangers geresolveerd naar employeeId + naam voor de picker. */
  verantwoordelijken: LoonstrookVerantwoordelijkeDTO[]
  /**
   * Goedgekeurde loonstroken automatisch inplannen op het standaard-moment
   * (batch-66, FO-492: de trigger is de goedkeuring, niet meer de upload).
   */
  autoVrijgaveActief: boolean
  /** Bedrijfsbrede standaard vrijgave-dag (1-28, NL-tijd), of null. */
  geplandeVrijgaveStandaardDag: number | null
  /** Bedrijfsbrede standaard vrijgave-uur (0-23, NL-tijd), of null. */
  geplandeVrijgaveStandaardUur: number | null
  /** batch-66 (FO-492) — gedrag bij een goedgekeurde strook met een verstreken moment. */
  verstrekenMomentGedrag: LoonstrookVerstrekenMomentGedrag
  // ── batch-56 (FO-417) — salarisverwerker + beoordelaars ────────────────────
  /** Naam van de externe salarisverwerker (alleen voor de UI/mailtekst). */
  salarisverwerkerNaam: string | null
  /** Hoofdontvangers van afkeur-/doorzet-mails. Leeg = de actie weigert. */
  salarisverwerkerTo: string[]
  /** CC-ontvangers van afkeur-/doorzet-mails. */
  salarisverwerkerCc: string[]
  /** Opgeslagen beoordelaar-userIds (rauw, voor round-trip). */
  verzoekBeoordelaarUserIds: string[]
  /** Dezelfde beoordelaars geresolveerd naar employeeId + naam voor de picker. */
  verzoekBeoordelaars: LoonstrookVerantwoordelijkeDTO[]
}

// ─── Correctieverzoek / vraag (spec #28 Deel 4) ──────────────────────────────
//
// De medewerker dient vanuit de portal een vraag of correctieverzoek in bij een
// (eigen, klaargezette) loonstrook. De loonstrook-beheerders (magLoonstrokenZien)
// krijgen een notificatie en handelen het in de backoffice af. Gedeeld
// request-schema: het portal-form valideert er client-side mee en de API
// .parse't ermee (één bron van waarheid).

export const LOONSTROOK_VERZOEK_TYPE = ['VRAAG', 'CORRECTIE'] as const
export type LoonstrookVerzoekType = (typeof LOONSTROOK_VERZOEK_TYPE)[number]

export const LOONSTROOK_VERZOEK_STATUS = ['OPEN', 'BEANTWOORD', 'AFGEHANDELD'] as const
export type LoonstrookVerzoekStatus = (typeof LOONSTROOK_VERZOEK_STATUS)[number]

export const LOONSTROOK_VERZOEK_TYPE_LABELS: Record<LoonstrookVerzoekType, string> = {
  VRAAG: 'Vraag',
  CORRECTIE: 'Correctieverzoek',
}

export const LOONSTROOK_VERZOEK_STATUS_LABELS: Record<LoonstrookVerzoekStatus, string> = {
  OPEN: 'Open',
  BEANTWOORD: 'Beantwoord',
  AFGEHANDELD: 'Afgehandeld',
}

/** Body bij POST /loonstroken/mijn/:id/verzoek (portal, medewerker dient in). */
export const loonstrookVerzoekCreateSchema = z.object({
  type: z.enum(LOONSTROOK_VERZOEK_TYPE),
  bericht: z.string().trim().min(1, 'Vul je vraag of verzoek in').max(2000),
})
export type LoonstrookVerzoekCreateInput = z.infer<typeof loonstrookVerzoekCreateSchema>

/** Body bij POST /payroll/loonstroken/verzoeken/:id/afhandelen (backoffice beheerder). */
export const loonstrookVerzoekAfhandelenSchema = z.object({
  // Alleen de twee afhandel-statussen; OPEN is de begintoestand, niet instelbaar.
  status: z.enum(['BEANTWOORD', 'AFGEHANDELD']),
  antwoord: z.string().trim().min(1, 'Vul een reactie in').max(2000),
})
export type LoonstrookVerzoekAfhandelenInput = z.infer<typeof loonstrookVerzoekAfhandelenSchema>

// ─── batch-56 loonstroken (FO-417) — request-schemas ─────────────────────────
//
// Gedeeld tussen API (.safeParse -> sendValidationError met issues[]) en de
// backoffice-formulieren (client-side .safeParse + issuesToFieldErrors), zodat
// een form nooit iets kan sturen dat de API weigert.

/** Eén bevinding in het afkeur-formulier: BEIDE velden zijn verplicht (§6.1). */
export const loonstrookBevindingSchema = z.object({
  categorie: z.enum(LOONSTROOK_BEVINDING_CATEGORIE).default('OVERIG'),
  watKloptNiet: z
    .string()
    .trim()
    .min(3, 'Beschrijf wat er niet klopt')
    .max(2000),
  gewensteAanpassing: z
    .string()
    .trim()
    .min(3, 'Beschrijf wat er aangepast moet worden')
    .max(2000),
})
export type LoonstrookBevindingInput = z.infer<typeof loonstrookBevindingSchema>

/**
 * Body bij POST /payroll/loonstroken/:id/afkeuren. Minimaal één bevinding -
 * afkeuren zonder opgave van wat er niet klopt kan niet (§6.1).
 */
export const loonstrookAfkeurenSchema = z.object({
  bevindingen: z
    .array(loonstrookBevindingSchema)
    .min(1, 'Voeg minimaal één bevinding toe')
    .max(25),
})
export type LoonstrookAfkeurenInput = z.infer<typeof loonstrookAfkeurenSchema>

/**
 * Body bij POST /payroll/loonstroken/:id/run-keuze (FO-422 §3.2). `TOEVOEGEN`
 * bevestigt de plaatsing als extra run; `CORRECTIE` verhuist deze strook als
 * nieuwe VERSIE naar de aangewezen bestaande run (`doelLoonstrookId`).
 */
export const loonstrookRunKeuzeSchema = z
  .object({
    keuze: z.enum(LOONSTROOK_RUN_KEUZE),
    /** Verplicht bij CORRECTIE: de run waarvan deze strook een nieuwe versie wordt. */
    doelLoonstrookId: idParam('doelLoonstrookId').nullable().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.keuze === 'CORRECTIE' && !v.doelLoonstrookId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['doelLoonstrookId'],
        message: 'Kies de strook die deze correctie vervangt',
      })
    }
  })
export type LoonstrookRunKeuzeInput = z.infer<typeof loonstrookRunKeuzeSchema>

/**
 * Eén correctie op een NMBRS-looncode in de mail naar de salarisverwerker. De
 * correctie is korte tekst omdat de codes zowel uren als bedragen zijn
 * ("+8 uur", "€ 150,00 bruto").
 */
export const loonstrookLooncodeCorrectieSchema = z.object({
  code: z
    .string()
    .refine((c) => (NMBRS_KOLOM_CODES as readonly string[]).includes(c), 'Onbekende looncode'),
  correctie: z.string().trim().min(1, 'Vul de correctie in').max(200),
})
export type LoonstrookLooncodeCorrectie = z.infer<typeof loonstrookLooncodeCorrectieSchema>

/**
 * Wat er naar de salarisverwerker gaat: de looncodes en een toelichting van de
 * beoordelaar. Het bericht van de medewerker hoort hier bewust niet bij.
 */
const loonstrookDoorzetVelden = {
  looncodes: z.array(loonstrookLooncodeCorrectieSchema).max(50).default([]),
  toelichting: z.string().trim().max(2000).nullable().optional(),
}

const DOORZET_LEEG_MELDING =
  'Vul minimaal één looncode of een toelichting in; het bericht van de medewerker gaat niet mee naar de salarisadministratie'

function doorzetIsLeeg(v: { looncodes: unknown[]; toelichting?: string | null }): boolean {
  return v.looncodes.length === 0 && !(v.toelichting ?? '').trim()
}

/**
 * Body bij POST /payroll/loonstroken/verzoeken/:id/besluit (FO-417 §3.5).
 * Goedkeuren kan met of zonder doorzetten naar de salarisverwerker; doorzetten
 * vraagt om looncodes of een toelichting. `reden` is de reactie die de
 * medewerker leest en is verplicht bij afwijzen (§6 T5).
 */
export const loonstrookVerzoekBesluitSchema = z
  .object({
    besluit: z.enum(LOONSTROOK_VERZOEK_BESLUIT),
    reden: z.string().trim().max(2000).nullable().optional(),
    doorzetten: z.boolean().default(false),
    ...loonstrookDoorzetVelden,
  })
  .superRefine((v, ctx) => {
    if (v.besluit === 'AFGEWEZEN' && !(v.reden ?? '').trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['reden'],
        message: 'Geef een reden op; de medewerker krijgt deze te zien',
      })
    }
    if (v.besluit === 'GOEDGEKEURD' && v.doorzetten && doorzetIsLeeg(v)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['looncodes'],
        message: DOORZET_LEEG_MELDING,
      })
    }
  })
export type LoonstrookVerzoekBesluitInput = z.input<typeof loonstrookVerzoekBesluitSchema>

/**
 * Body bij POST /payroll/loonstroken/verzoeken/:id/doorzetten. Leeg = verstuur
 * wat bij het besluit is vastgelegd (de vorige mail is niet aangekomen); ingevuld
 * = een goedgekeurd verzoek dat eerst niet doorgezet is, alsnog doorzetten.
 */
export const loonstrookVerzoekDoorzettenSchema = z.object(loonstrookDoorzetVelden)
export type LoonstrookVerzoekDoorzettenInput = z.input<typeof loonstrookVerzoekDoorzettenSchema>

/**
 * Eén verzoek bij een loonstrook. Bevat geen loonbedragen/BSN - alleen de vrije
 * tekst van de medewerker + de reactie van de beheerder + workflow-metadata.
 */
export interface LoonstrookVerzoekDTO {
  id: string
  loonstrookId: string
  type: LoonstrookVerzoekType
  status: LoonstrookVerzoekStatus
  bericht: string
  antwoord: string | null
  /** Display-naam van de medewerker (backoffice-context); null in de portal. */
  medewerkerNaam: string | null
  /** Display-naam van de beheerder die afhandelde, of null. */
  afgehandeldDoorNaam: string | null
  createdAt: string
  afgehandeldOp: string | null
  /** batch-56 (FO-417) — beoordelingsbesluit, of null zolang niet beoordeeld. */
  besluit: LoonstrookVerzoekBesluit | null
  /** Display-naam van de beoordelaar, of null. */
  besluitDoorNaam: string | null
  besluitOp: string | null
  /**
   * Reden bij AFWIJZEN - de medewerker krijgt deze te zien. Bevat nooit interne
   * beoordelaars-discussie (§6.6).
   */
  besluitReden: string | null
  /** ISO datetime waarop het verzoek naar de salarisverwerker is doorgezet. */
  doorgezetOp: string | null
  /**
   * Alleen backoffice: is doorzetten gekozen en wat ging of gaat er naar de
   * salarisverwerker. De portal krijgt dit nooit.
   */
  naarSalarisverwerker?: boolean
  salarisverwerkerLooncodes?: LoonstrookLooncodeCorrectie[]
  salarisverwerkerToelichting?: string | null
}

// ─── batch-61 loonstrook-dedupe (FO-456) — vingerafdruk, aanleveringen, mailbox ─
//
// Inhoud-vingerafdruk-dedupe over alle importpaden. De pure vingerafdruk zelf
// staat in @streep/utils (`berekenLoonstrookVingerafdruk`); hier de enums en
// DTO's voor het aanleverlog per strook en de mailbox-ingest-lijst. Geen
// loonbedragen, geen BSN, geen bestandsnamen (die kunnen een medewerkernaam
// bevatten) in deze DTO's.

/** Status van een verloning-mailbox-ingest (spiegelt de Prisma-enum). */
export const LOONSTROOK_MAIL_INGEST_STATUS = [
  'NIEUW',
  'GEEN_BIJLAGE',
  'VERWERKT',
  'TE_CONTROLEREN',
  'FOUT',
  'QUARANTAINE',
  // batch-61 (FO-456 §3.4) — de mail bracht niets nieuws: alle bijlagen stonden
  // er al (zelfde bytes of zelfde inhoud). Nooit stil: zichtbaar in de lijst.
  'DUBBELE_AANLEVERING',
] as const
export type LoonstrookMailIngestStatus = (typeof LOONSTROOK_MAIL_INGEST_STATUS)[number]

export const LOONSTROOK_MAIL_INGEST_STATUS_LABELS: Record<LoonstrookMailIngestStatus, string> = {
  NIEUW:               'Nieuw (nog te verwerken)',
  GEEN_BIJLAGE:        'Geen PDF-bijlage',
  VERWERKT:            'Verwerkt',
  TE_CONTROLEREN:      'Te controleren',
  FOUT:                'Fout',
  QUARANTAINE:         'Quarantaine (malware-scan)',
  DUBBELE_AANLEVERING: 'Dubbele aanlevering',
}

/** Via welk pad een strook binnenkwam. */
export const LOONSTROOK_AANLEVER_PAD = ['MAILBOX', 'UPLOAD', 'WIZARD', 'KOPPELING', 'OPSCHOONRUN'] as const
export type LoonstrookAanleverPad = (typeof LOONSTROOK_AANLEVER_PAD)[number]

export const LOONSTROOK_AANLEVER_PAD_LABELS: Record<LoonstrookAanleverPad, string> = {
  MAILBOX:     'verloning-mailbox',
  UPLOAD:      'losse upload',
  WIZARD:      'import-wizard',
  KOPPELING:   'handmatig koppelen',
  OPSCHOONRUN: 'opschoonrun',
}

/** Wat er met een aanlevering gebeurd is. */
export const LOONSTROOK_AANLEVER_UITKOMST = [
  'NIEUW',
  'VERSIE',
  'DUBBEL_BYTES',
  'DUBBEL_INHOUD',
  'SAMENGEVOEGD',
] as const
export type LoonstrookAanleverUitkomst = (typeof LOONSTROOK_AANLEVER_UITKOMST)[number]

export const LOONSTROOK_AANLEVER_UITKOMST_LABELS: Record<LoonstrookAanleverUitkomst, string> = {
  NIEUW:         'Nieuwe strook',
  VERSIE:        'Gecorrigeerde uitgave (nieuwe versie)',
  DUBBEL_BYTES:  'Dubbel aangeleverd (zelfde bestand)',
  DUBBEL_INHOUD: 'Dubbel aangeleverd (zelfde inhoud, ander bestand)',
  SAMENGEVOEGD:  'Dubbele strook samengevoegd',
}

/** Eén regel uit het aanleverlog van een strook. */
export interface LoonstrookAanleveringDTO {
  id: string
  /** ISO datetime. */
  aangeleverdOp: string
  pad: LoonstrookAanleverPad
  padLabel: string
  uitkomst: LoonstrookAanleverUitkomst
  uitkomstLabel: string
  /** Mail-ingest van herkomst, of null. */
  ingestId: string | null
  /** Bij SAMENGEVOEGD: de strook die hierin is opgegaan. */
  bronLoonstrookId: string | null
  doorUserId: string | null
  doorNaam: string | null
  toelichting: string | null
}

/**
 * Dedupe-uitkomst per bijlage/pagina van een mail-ingest (spec §3.6). Naast de
 * aanlever-uitkomsten ook de redenen waarom een pagina NIET ingelezen is, zodat
 * de ingest-lijst per pagina laat zien wat ermee gebeurd is.
 */
export const LOONSTROOK_INGEST_BIJLAGE_UITKOMST = [
  'NIEUW',
  'VERSIE',
  'DUBBEL_BYTES',
  'DUBBEL_INHOUD',
  'KEUZE_VEREIST', // nieuw als extra run, maar met openstaande FO-422-keuze
  'GEEN_PERIODE',
  'EXTRACTIE_MISLUKT',
] as const
export type LoonstrookIngestBijlageUitkomst = (typeof LOONSTROOK_INGEST_BIJLAGE_UITKOMST)[number]

export const LOONSTROOK_INGEST_BIJLAGE_UITKOMST_LABELS: Record<LoonstrookIngestBijlageUitkomst, string> = {
  NIEUW:             'Nieuwe strook',
  VERSIE:            'Gecorrigeerde uitgave',
  DUBBEL_BYTES:      'Dubbel (zelfde bestand)',
  DUBBEL_INHOUD:     'Dubbel (zelfde inhoud)',
  KEUZE_VEREIST:     'Extra run, keuze open',
  GEEN_PERIODE:      'Geen loonstrook herkend',
  EXTRACTIE_MISLUKT: 'Extractie mislukt',
}

/** Eén bijlage/pagina in een mail-ingest met zijn dedupe-uitkomst. Geen bestandsnaam. */
export interface LoonstrookIngestBijlageUitkomstDTO {
  /** 1-gebaseerde bijlage-index binnen de mail. */
  bijlage: number
  /** 1-gebaseerde pagina binnen de bijlage. */
  pagina: number
  uitkomst: LoonstrookIngestBijlageUitkomst
  /** De (bestaande of nieuwe) strook waar deze pagina bij hoort, of null. */
  loonstrookId: string | null
}

export const LoonstrookIngestBijlageUitkomstSchema = z.object({
  bijlage: z.number().int().min(1),
  pagina: z.number().int().min(1),
  uitkomst: z.enum(LOONSTROOK_INGEST_BIJLAGE_UITKOMST),
  loonstrookId: z.string().nullable(),
})
export const LoonstrookIngestBijlageUitkomstenSchema = z.array(LoonstrookIngestBijlageUitkomstSchema)

/**
 * Eén rij in de verloning-mailbox-ingest-lijst (backoffice). Mail-metadata
 * (afzender/onderwerp, zoals al op de ingest-rij) + tellingen + per-pagina
 * uitkomsten; geen loonbedragen, geen BSN, geen bestandsnamen van bijlagen.
 */
export interface LoonstrookMailIngestDTO {
  id: string
  mailbox: string
  afzender: string | null
  onderwerp: string | null
  /** ISO datetime, of null. */
  ontvangenOp: string | null
  status: LoonstrookMailIngestStatus
  statusLabel: string
  aantalBijlagen: number
  /** Niet-gevoelige samenvatting/reden (tellingen), of null. */
  foutmelding: string | null
  verwerktOp: string | null
  importBatchId: string | null
  /** De eerdere ingest met dezelfde bijlage-set, of null. */
  dubbeleAanleveringVanId: string | null
  /** Per bijlage/pagina de dedupe-uitkomst; leeg wanneer (nog) niet verwerkt. */
  bijlagen: LoonstrookIngestBijlageUitkomstDTO[]
  /** Aantal stroken dat deze mail daadwerkelijk toevoegde (nieuw + versie). */
  strokenAangemaakt: number
  createdAt: string
}

/** Query bij GET /payroll/loonstroken/mailbox-ingests. */
export const loonstrookMailIngestFilterSchema = z.object({
  status: z.enum(LOONSTROOK_MAIL_INGEST_STATUS).optional(),
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().max(100).default(25),
})
export type LoonstrookMailIngestFilter = z.infer<typeof loonstrookMailIngestFilterSchema>

/** Waarop een binnenkomst als dubbel herkend is. */
export const LOONSTROOK_DEDUPE_VIA = ['bytes', 'inhoud'] as const
export type LoonstrookDedupeVia = (typeof LOONSTROOK_DEDUPE_VIA)[number]

/**
 * Meta bij PATCH /payroll/loonstroken/:id/koppeling (spec §3.3): is de
 * gekoppelde strook een dubbel van een strook die de medewerker al had, dan is
 * hij daarin samengevoegd (document als extra versie, geen tweede strook) en
 * wijst `samengevoegdMet` naar de behouden strook; de respons-`data` is dan het
 * detail van die behouden strook.
 */
export interface LoonstrookKoppelMetaDTO {
  samengevoegdMet: string | null
  samengevoegdVia: LoonstrookDedupeVia | null
}

/** Meta bij POST /payroll/loonstroken/upload. */
export interface LoonstrookUploadMetaDTO {
  uitkomst: 'created' | 'versioned' | 'dedupe'
  /** Alleen bij `dedupe`: waarop de dubbel herkend is. */
  dedupeVia: LoonstrookDedupeVia | null
}
